import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  User,
  Lock,
  Eye,
  EyeOff,
  AlertCircle,
  AlertTriangle,
  ShieldCheck,
  LogIn,
  FileText ,
} from 'lucide-react';
import './Login.css';
import api from '../api/axiosConfig';

function Login({ onLogin }) {
  const [userId, setUserId]         = useState('');
  const [password, setPassword]     = useState('');
  const [error, setError]           = useState('');
  const [loading, setLoading]       = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [userIdError, setUserIdError]   = useState('');
  const [passwordError, setPasswordError] = useState('');
  const navigate = useNavigate();

  /* ── 유효성 검사 ── */
  const validateForm = () => {
    let isValid = true;
    setUserIdError('');
    setPasswordError('');

    if (!userId.trim()) {
      setUserIdError('사용자 이름 또는 이메일을 입력해주세요');
      isValid = false;
    }

    if (!password) {
      setPasswordError('비밀번호를 입력해주세요');
      isValid = false;
    }
    // ✂️ password.length < 6 체크 제거
    // → 비밀번호 길이 힌트는 보안상 서버에서 통일 처리

    return isValid;
  };

  /* ── 로그인 요청 ── */
  const handleLogin = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setError('');
    setLoading(true);

    try {
      localStorage.removeItem('token');
      localStorage.removeItem('userId');

      const response = await api.post('/api/login/userAuth', {
        userId,
        password,
      });

      const token = response.data.token;

      localStorage.setItem('token', token);
      localStorage.setItem('userId', userId);

      onLogin(userId);
      navigate('/home');
    } catch (err) {
      console.error('로그인 실패', err);
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data ||
        '아이디 또는 비밀번호가 올바르지 않습니다.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  /* ── 비밀번호 찾기 ── */
  const handleForgotPassword = (e) => {
    e.preventDefault();
    navigate('/forgot-password');
  };

  /* ── 아이디 변경 핸들러 ── */
  const handleUserIdChange = (e) => {
    setUserId(e.target.value);
    if (userIdError) setUserIdError('');
  };

  /* ── 비밀번호 변경 핸들러 ── */
  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    if (passwordError) setPasswordError('');
  };

  return (
    <div className="github-login-container">

      {/* ── 로고 ── */}
      <div className="github-logo">
        <div className="logo-content">
          <span className="logo-emoji">
            <FileText  size={20} strokeWidth={1.8} color="#ffffff" />
          </span>
          <div className="logo-text-group">
            <span className="logo-text">E-BILL</span>
            <span className="logo-sub">세금계산서 관리 시스템</span>
          </div>
        </div>
      </div>

      {/* ── 카드 ── */}
      <div className="github-login-card">
        <h1 className="github-heading">계정에 로그인하세요</h1>

        <form onSubmit={handleLogin} noValidate>

          {/* 아이디 */}
          <div className="form-group">
            <label htmlFor="userId">사용자 이름 또는 이메일 주소</label>
            <div className="input-wrapper">
              <span className="input-icon" aria-hidden="true">
                <User size={15} strokeWidth={1.8} />
              </span>
              <input
                type="text"
                id="userId"
                className={`github-input${userIdError ? ' input-error' : ''}`}
                value={userId}
                onChange={handleUserIdChange}
                onFocus={() => setError('')}
                placeholder="example@email.com 또는 username"
                autoComplete="username"
                autoFocus
              />
            </div>
            {userIdError && (
              <p className="field-error" role="alert">
                <AlertTriangle size={13} strokeWidth={2} aria-hidden="true" />
                {userIdError}
              </p>
            )}
          </div>

          {/* 비밀번호 */}
          <div className="form-group">
            <div className="password-header">
              <label htmlFor="password">비밀번호</label>
              <a
                href="/forgot-password"
                onClick={handleForgotPassword}
                className="forgot-password"
              >
                비밀번호를 잊으셨나요?
              </a>
            </div>
            <div className="input-wrapper">
              <span className="input-icon" aria-hidden="true">
                <Lock size={15} strokeWidth={1.8} />
              </span>
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                className={`github-input${passwordError ? ' input-error' : ''}`}
                value={password}
                onChange={handlePasswordChange}
                onFocus={() => setError('')}
                placeholder="비밀번호를 입력하세요"
                autoComplete="current-password"
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={showPassword ? '비밀번호 숨기기' : '비밀번호 보기'}
              >
                {showPassword
                  ? <EyeOff size={15} strokeWidth={1.8} />
                  : <Eye    size={15} strokeWidth={1.8} />
                }
              </button>
            </div>
            {passwordError && (
              <p className="field-error" role="alert">
                <AlertTriangle size={13} strokeWidth={2} aria-hidden="true" />
                {passwordError}
              </p>
            )}
          </div>

          {/* 전체 에러 */}
          {error && (
            <div className="error-container" role="alert">
              <p className="github-error">
                <AlertCircle size={15} strokeWidth={2} aria-hidden="true" />
                {error}
              </p>
            </div>
          )}

          {/* 로그인 버튼 */}
          <button
            type="submit"
            className="github-button"
            disabled={loading}
          >
            {loading ? (
              <span className="button-loading">
                <span className="spinner" aria-hidden="true" />
                로그인 중...
              </span>
            ) : (
              <>
                <LogIn size={15} strokeWidth={2} aria-hidden="true" />
                로그인
              </>
            )}
          </button>

        </form>

        {/* 보안 안내 */}
        <div className="login-divider" />
        <div className="login-info">
          <ShieldCheck size={13} strokeWidth={2} aria-hidden="true" />
          <p>보안 연결로 보호됩니다</p>
        </div>

      </div>
    </div>
  );
}

export default Login;